import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supplementsAPI } from '../services/api'
import { HeartIcon, PlusIcon, CameraIcon, XMarkIcon, TrashIcon, DocumentTextIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, LightBulbIcon } from '@heroicons/react/24/outline'
import SupplementPhotoUpload from '../components/supplements/SupplementPhotoUpload'
import toast, { Toaster } from 'react-hot-toast'
import { contentLanguage } from '../i18n/supported'

export default function Supplements() {
  const { t, i18n } = useTranslation()
  const _navigate = useNavigate()
  const [showPhotoUpload, setShowPhotoUpload] = useState(false)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [analysisResult, setAnalysisResult] = useState(null)
  const [_editableAnalysis, setEditableAnalysis] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [editingSupplement, setEditingSupplement] = useState(null)
  const [editForm, setEditForm] = useState({
    morning_quantity: null,
    midday_quantity: null,
    evening_quantity: null,
    quantity_unit: '',
    start_date: '',
    end_date: '',
    notes: '',
    // Legacy fields (for backwards compatibility)
    frequency: '',
    time_of_day: '',
    default_quantity: ''
  })
  const [manualForm, setManualForm] = useState({
    name: '',
    brand: '',
    category: 'other',
    dosage_form: 'capsule',
    serving_size: '',
    morning_quantity: 1.0,
    midday_quantity: null,
    evening_quantity: null,
    quantity_unit: 'serving',
    start_date: new Date().toISOString().split('T')[0],
    notes: ''
  })
  const queryClient = useQueryClient()

  // Knowledge insights state
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false)
  const [expandedKnowledge, setExpandedKnowledge] = useState({})
  const [knowledgeData, setKnowledgeData] = useState({})

  const { data: supplements, isLoading } = useQuery({
    queryKey: ['user-supplements'],
    queryFn: supplementsAPI.getUserSupplements
  })

  // Knowledge status polling
  const { data: knowledgeStatusData, refetch: refetchKnowledgeStatus } = useQuery({
    queryKey: ['knowledge-status'],
    queryFn: supplementsAPI.getKnowledgeStatus,
    refetchInterval: isGeneratingInsights ? 3000 : false,
  })

  // Stop polling when all done
  useEffect(() => {
    if (knowledgeStatusData?.data && isGeneratingInsights) {
      const { processing, pending, not_started } = knowledgeStatusData.data
      if (processing === 0 && pending === 0 && not_started === 0) {
        setIsGeneratingInsights(false)
        toast.success(t('supplements.knowledge.generationComplete'))
      }
    }
  }, [knowledgeStatusData, isGeneratingInsights, t])

  // Re-translate expanded knowledge when language changes
  useEffect(() => {
    const retranslateKnowledge = async () => {
      const expandedIds = Object.keys(knowledgeData).filter(id => knowledgeData[id])
      if (expandedIds.length === 0) return

      const currentLang = contentLanguage(i18n?.language)

      for (const supplementId of expandedIds) {
        try {
          if (currentLang !== 'en') {
            const response = await supplementsAPI.translateKnowledge(supplementId, currentLang)
            setKnowledgeData(prev => ({ ...prev, [supplementId]: response.data }))
          } else {
            const response = await supplementsAPI.getKnowledge(supplementId, 'en')
            setKnowledgeData(prev => ({ ...prev, [supplementId]: response.data }))
          }
        } catch (err) {
          console.warn(`[Supplements] Could not translate knowledge for ${supplementId}:`, err)
        }
      }
    }

    retranslateKnowledge()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- knowledgeData is fetched separately; including it would cause unnecessary re-translations
  }, [i18n.language])

  // Mutation to save supplement
  const saveMutation = useMutation({
    mutationFn: (supplementData) => supplementsAPI.addToRegimen(supplementData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-supplements'] })
    }
  })

  // Mutation to update supplement
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => supplementsAPI.updateUserSupplement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-supplements'] })
      setEditingSupplement(null)
      setEditForm({
        morning_quantity: null,
        midday_quantity: null,
        evening_quantity: null,
        quantity_unit: '',
        start_date: '',
        end_date: '',
        notes: '',
        // Legacy fields
        frequency: '',
        time_of_day: '',
        default_quantity: ''
      })
    }
  })

  // Mutation to delete supplement
  const deleteMutation = useMutation({
    mutationFn: (id) => supplementsAPI.deleteUserSupplement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-supplements'] })
    }
  })

  // State for translated notes (keyed by user_supplement_id)
  const [translatedNotes, setTranslatedNotes] = useState({})

  // Fetch translated notes when supplements load or language changes
  useEffect(() => {
    const fetchTranslatedNotes = async () => {
      if (!supplements?.data?.user_supplements) return

      // Normalize language code (e.g., "de-DE" -> "de", "en-US" -> "en")
      const currentLang = contentLanguage(i18n?.language)
      const supplementsWithNotes = supplements.data.user_supplements.filter(us => us.notes)

      // If English, use original notes (no translation needed)
      if (currentLang === 'en') {
        const notesMap = {}
        supplementsWithNotes.forEach(us => {
          notesMap[us.id] = us.notes
        })
        setTranslatedNotes(notesMap)
        return
      }

      // Fetch translations for non-English languages
      const translations = {}
      for (const userSupplement of supplementsWithNotes) {
        try {
          const response = await supplementsAPI.translateNotes(userSupplement.id, currentLang)
          translations[userSupplement.id] = response.data.translated_notes
        } catch (err) {
          console.warn(`[Supplements] Could not translate notes for ${userSupplement.id}:`, err)
          translations[userSupplement.id] = userSupplement.notes // Fallback to original
        }
      }
      setTranslatedNotes(translations)
    }

    fetchTranslatedNotes()
  }, [supplements?.data?.user_supplements, i18n.language])

  const handleAnalysisComplete = (result) => {
    console.log('Analysis complete:', result)
    setAnalysisResult(result)
    // No need for editableAnalysis anymore - we use analysisResult directly
  }

  const handleSaveSupplement = async () => {
    if (!analysisResult) return

    setIsSaving(true)
    try {
      // Fetch original English data for saving to catalog
      // This ensures the catalog always contains English data regardless of UI language
      let dataForCatalog = analysisResult
      if (analysisResult.photo_analysis_id) {
        try {
          const englishResponse = await supplementsAPI.translateAnalysis(
            analysisResult.photo_analysis_id,
            'en'
          )
          dataForCatalog = englishResponse.data
          console.log('[Supplements] Using English data for catalog:', dataForCatalog.supplement_name)
        } catch (err) {
          console.warn('[Supplements] Could not fetch English version, using current data:', err)
          // Fall back to current analysisResult if English fetch fails
        }
      }

      // Step 1: Create supplement in catalog using English AI analysis results
      const ingredientNames = (dataForCatalog.active_ingredients || []).map(ing =>
        `${ing.name} (${ing.amount})`
      )

      const catalogData = {
        name: dataForCatalog.supplement_name || 'Unknown Supplement',
        brand: dataForCatalog.brand || null,
        category: dataForCatalog.category || 'other',
        dosage_form: dataForCatalog.dosage_form || 'capsule',
        description: `${dataForCatalog.supplement_name || 'Unknown'}${dataForCatalog.brand ? ` by ${dataForCatalog.brand}` : ''}. ${dataForCatalog.recommended_dosage || ''}`,
        dosage_amount: parseFloat(dataForCatalog.servings_per_container) || 1.0,
        dosage_unit: 'serving',
        active_ingredients: ingredientNames.length > 0 ? ingredientNames.join(', ') : 'Not detected',
        is_active: true
      }

      console.log('Saving supplement catalog data:', catalogData)
      const catalogResponse = await supplementsAPI.create(catalogData)
      console.log('Catalog response:', catalogResponse)
      const supplementId = catalogResponse.data.id

      // Step 2: Add to user's regimen with DEFAULT schedule (1 serving in morning)
      // Use English data for notes to ensure consistent storage
      const regimenData = {
        supplement_id: supplementId,
        morning_quantity: 1.0,  // Default: 1 serving in morning
        midday_quantity: null,
        evening_quantity: null,
        quantity_unit: 'serving',
        start_date: new Date().toISOString().split('T')[0],  // date format YYYY-MM-DD
        enable_daily_logging: false,
        is_active: true,
        // Notes stored in English (base language) for multi-language consistency
        // Data values from English source (dataForCatalog)
        notes: `Added from photo analysis. Serving size: ${dataForCatalog.serving_size || 'N/A'}. Servings per container: ${dataForCatalog.servings_per_container || 'N/A'}. ${dataForCatalog.recommended_dosage || ''}`,
        // Legacy fields (required by database constraint, will be removed in future migration)
        frequency: 'once_daily',  // Must match FrequencyEnum.ONCE_DAILY
        time_of_day: 'morning'    // Must match TimeOfDayEnum.MORNING
      }

      await saveMutation.mutateAsync(regimenData)

      // Show success and close
      toast.success(t('supplements.messages.savedSuccess'))
      setAnalysisResult(null)
      setEditableAnalysis(null)
      setShowPhotoUpload(false)
      setIsSaving(false)
    } catch (error) {
      console.error('Error saving supplement:', error)
      toast.error(t('supplements.messages.saveFailed') + ': ' + (error.response?.data?.detail || error.message))
      setIsSaving(false)
    }
  }

  const handleCloseModal = () => {
    setShowPhotoUpload(false)
    setAnalysisResult(null)
    setEditableAnalysis(null)
  }

  const handleSaveManualEntry = async () => {
    // Validation
    if (!manualForm.name || !manualForm.name.trim()) {
      toast.error(t('supplements.messages.enterName'))
      return
    }

    const total = (manualForm.morning_quantity || 0) +
                  (manualForm.midday_quantity || 0) +
                  (manualForm.evening_quantity || 0)

    if (total === 0) {
      toast.error(t('supplements.messages.setAtLeastOneTime'))
      return
    }

    setIsSaving(true)
    try {
      // Step 1: Create supplement in catalog
      const catalogData = {
        name: manualForm.name.trim(),
        brand: manualForm.brand?.trim() || null,
        category: manualForm.category,
        dosage_form: manualForm.dosage_form,
        description: `${manualForm.name}${manualForm.brand ? ` by ${manualForm.brand}` : ''}`,
        dosage_amount: 1.0,
        dosage_unit: 'serving',
        active_ingredients: manualForm.serving_size || 'Not specified',
        is_active: true
      }

      console.log('Saving manual supplement:', catalogData)
      const catalogResponse = await supplementsAPI.create(catalogData)
      const supplementId = catalogResponse.data.id

      // Step 2: Add to user's regimen
      const regimenData = {
        supplement_id: supplementId,
        morning_quantity: manualForm.morning_quantity || null,
        midday_quantity: manualForm.midday_quantity || null,
        evening_quantity: manualForm.evening_quantity || null,
        quantity_unit: manualForm.quantity_unit,
        start_date: manualForm.start_date,
        enable_daily_logging: false,
        is_active: true,
        notes: manualForm.notes || '',
        frequency: 'once_daily',
        time_of_day: 'morning'
      }

      await saveMutation.mutateAsync(regimenData)

      // Success - close and reset
      toast.success(t('supplements.messages.addedSuccess'))
      setShowManualEntry(false)
      setManualForm({
        name: '',
        brand: '',
        category: 'other',
        dosage_form: 'capsule',
        serving_size: '',
        morning_quantity: 1.0,
        midday_quantity: null,
        evening_quantity: null,
        quantity_unit: 'serving',
        start_date: new Date().toISOString().split('T')[0],
        notes: ''
      })
      setIsSaving(false)
    } catch (error) {
      console.error('Error saving manual supplement:', error)
      toast.error(t('supplements.messages.saveFailed') + ': ' + (error.response?.data?.detail || error.message))
      setIsSaving(false)
    }
  }

  const handleEditClick = (userSupplement) => {
    setEditingSupplement(userSupplement)
    setEditForm({
      morning_quantity: userSupplement.morning_quantity || null,
      midday_quantity: userSupplement.midday_quantity || null,
      evening_quantity: userSupplement.evening_quantity || null,
      quantity_unit: userSupplement.quantity_unit || '',
      start_date: userSupplement.start_date || '',
      end_date: userSupplement.end_date || '',
      notes: userSupplement.notes || '',
      // Legacy fields
      frequency: userSupplement.frequency || '',
      time_of_day: userSupplement.time_of_day || '',
      default_quantity: userSupplement.default_quantity || ''
    })
  }

  const handleSaveEdit = () => {
    if (!editingSupplement) return

    // Validate that at least one time slot has a quantity
    const total = (editForm.morning_quantity || 0) +
                  (editForm.midday_quantity || 0) +
                  (editForm.evening_quantity || 0)

    if (total === 0) {
      toast.error(t('supplements.messages.setAtLeastOneTime'))
      return
    }

    // Validate dates
    if (editForm.start_date && editForm.end_date) {
      const startDate = new Date(editForm.start_date)
      const endDate = new Date(editForm.end_date)
      if (endDate < startDate) {
        toast.error(t('supplements.messages.endDateBeforeStart'))
        return
      }
    }

    const updateData = {
      morning_quantity: editForm.morning_quantity || null,
      midday_quantity: editForm.midday_quantity || null,
      evening_quantity: editForm.evening_quantity || null,
      quantity_unit: editForm.quantity_unit,
      notes: editForm.notes
    }

    // Include date fields
    if (editForm.start_date) {
      updateData.start_date = editForm.start_date
    }
    if (editForm.end_date) {
      updateData.end_date = editForm.end_date
    }

    updateMutation.mutate({
      id: editingSupplement.id,
      data: updateData
    })
  }

  const handleCancelEdit = () => {
    setEditingSupplement(null)
    setEditForm({
      morning_quantity: null,
      midday_quantity: null,
      evening_quantity: null,
      quantity_unit: '',
      start_date: '',
      end_date: '',
      notes: '',
      // Legacy fields
      frequency: '',
      time_of_day: '',
      default_quantity: ''
    })
  }

  const handleDeleteClick = (userSupplement) => {
    if (window.confirm(t('supplements.messages.confirmDelete', { name: userSupplement.supplement.name }))) {
      deleteMutation.mutate(userSupplement.id)
    }
  }

  const handleStopTaking = (userSupplement) => {
    if (window.confirm(t('supplements.messages.confirmStopTaking', { name: userSupplement.supplement.name }))) {
      const today = new Date().toISOString().split('T')[0]
      updateMutation.mutate({
        id: userSupplement.id,
        data: {
          end_date: today
        }
      })
    }
  }

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return null

    const start = new Date(startDate)
    const end = endDate ? new Date(endDate) : new Date()
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return t('supplements.duration.startedToday')
    if (diffDays === 1) return t('supplements.duration.oneDay')
    if (diffDays < 30) return t('supplements.duration.days', { count: diffDays })
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return months === 1 ? t('supplements.duration.oneMonth') : t('supplements.duration.months', { count: months })
    }
    const years = Math.floor(diffDays / 365)
    return years === 1 ? t('supplements.duration.oneYear') : t('supplements.duration.years', { count: years })
  }

  const isCurrentlyTaking = (userSupplement) => {
    if (!userSupplement.end_date) return true
    const endDate = new Date(userSupplement.end_date)
    const today = new Date()
    return endDate >= today
  }

  const _formatFrequency = (frequency) => {
    if (!frequency) return ''
    return frequency
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const _formatTimeOfDay = (timeOfDay) => {
    if (!timeOfDay) return ''
    return timeOfDay
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const calculateTotalConsumed = (userSupplement) => {
    if (!userSupplement.start_date) return null

    // Calculate total daily quantity from time-based quantities
    const dailyQuantity = (userSupplement.morning_quantity || 0) +
                          (userSupplement.midday_quantity || 0) +
                          (userSupplement.evening_quantity || 0)

    // Fallback to legacy default_quantity if new fields aren't set
    const totalDaily = dailyQuantity > 0 ? dailyQuantity : (userSupplement.default_quantity || 0)

    if (totalDaily === 0) return null

    const start = new Date(userSupplement.start_date)
    const end = userSupplement.end_date ? new Date(userSupplement.end_date) : new Date()
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 to include start day

    const totalConsumed = diffDays * totalDaily
    return {
      total: totalConsumed,
      dailyQuantity: totalDaily,
      unit: userSupplement.quantity_unit || 'units',
      days: diffDays
    }
  }

  const getTimeBasedSchedule = (userSupplement) => {
    const times = []
    if (userSupplement.morning_quantity && userSupplement.morning_quantity > 0) {
      times.push(`🌅 ${t('supplements.schedule.morning')}: ${userSupplement.morning_quantity}`)
    }
    if (userSupplement.midday_quantity && userSupplement.midday_quantity > 0) {
      times.push(`☀️ ${t('supplements.schedule.midday')}: ${userSupplement.midday_quantity}`)
    }
    if (userSupplement.evening_quantity && userSupplement.evening_quantity > 0) {
      times.push(`🌙 ${t('supplements.schedule.evening')}: ${userSupplement.evening_quantity}`)
    }

    if (times.length > 0) {
      return times.join(' • ')
    }

    // Fallback to legacy display
    if (userSupplement.default_quantity) {
      return `${userSupplement.default_quantity} ${userSupplement.quantity_unit || t('supplements.units')}`
    }

    return null
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <Toaster position="top-right" />
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('supplements.title')}</h1>
            <p className="mt-2 text-gray-600">
              {t('supplements.subtitle')}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPhotoUpload(true)}
              className="btn-primary flex items-center"
            >
              <CameraIcon className="h-5 w-5 mr-2" />
              {t('supplements.analyzePhoto')}
            </button>
            <button
              onClick={() => setShowManualEntry(true)}
              className="btn-outline flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('supplements.addManually')}
            </button>
          </div>
        </div>

        {/* Secondary actions row */}
        <div className="mt-4 flex gap-2">
          <Link
            to="/supplements/daily-report"
            className="btn-outline flex items-center text-sm"
          >
            <ClockIcon className="h-4 w-4 mr-1" />
            {t('supplements.dailyReport')}
          </Link>
          <Link
            to="/supplements/history-report"
            className="btn-outline flex items-center text-sm"
          >
            <DocumentTextIcon className="h-4 w-4 mr-1" />
            {t('supplements.historyReport')}
          </Link>
          <button
            onClick={async () => {
              setIsGeneratingInsights(true)
              try {
                await supplementsAPI.generateAllKnowledge()
                toast.success(t('supplements.knowledge.generating'))
                refetchKnowledgeStatus()
              } catch (err) {
                toast.error(t('supplements.knowledge.generationFailed'))
                setIsGeneratingInsights(false)
              }
            }}
            disabled={isGeneratingInsights}
            className="btn-primary flex items-center text-sm disabled:opacity-50"
          >
            <SparklesIcon className="h-4 w-4 mr-1" />
            {isGeneratingInsights ? t('supplements.knowledge.generating') : t('supplements.knowledge.generateInsights')}
          </button>
          {knowledgeStatusData?.data && knowledgeStatusData.data.total > 0 && (
            <span className="text-xs text-gray-500 self-center">
              {knowledgeStatusData.data.completed}/{knowledgeStatusData.data.total} {t('supplements.knowledge.enriched')}
              {knowledgeStatusData.data.processing > 0 && ` (${knowledgeStatusData.data.processing} ${t('supplements.knowledge.inProgress')})`}
            </span>
          )}
        </div>
      </div>

      {/* Photo Upload Modal */}
      {showPhotoUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-container border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {analysisResult ? t('supplements.modal.analysisResults') : t('supplements.modal.analyzePhoto')}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {!analysisResult ? (
                <SupplementPhotoUpload
                  onAnalysisComplete={handleAnalysisComplete}
                  onCancel={handleCloseModal}
                />
              ) : (
                <div className="space-y-6">
                  {/* Prominent Confidence Score */}
                  <div className={`border-2 rounded-lg p-4 ${
                    analysisResult.confidence >= 0.8 ? 'bg-green-50 border-green-500' :
                    analysisResult.confidence >= 0.6 ? 'bg-yellow-50 border-yellow-500' :
                    'bg-orange-50 border-orange-500'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-lg">
                          {t('supplements.modal.confidence')}: {(analysisResult.confidence * 100).toFixed(0)}%
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          {analysisResult.confidence >= 0.8 ? t('supplements.modal.confidenceHigh') :
                           analysisResult.confidence >= 0.6 ? t('supplements.modal.confidenceMedium') :
                           t('supplements.modal.confidenceLow')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-900 text-sm">
                      <strong>{t('supplements.modal.aiResults')}:</strong> {t('supplements.modal.reviewInfo')}
                    </p>
                  </div>

                  {analysisResult && (
                    <div className="space-y-6">
                      {/* Read-only Supplement Information */}
                      <div className="bg-surface-container border border-gray-200 rounded-lg p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('supplements.modal.supplementInfo')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.form.name')}</p>
                            <p className="text-base text-gray-900 font-medium">{analysisResult.supplement_name || t('supplements.modal.notDetected')}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.form.brand')}</p>
                            <p className="text-base text-gray-900">{analysisResult.brand || t('supplements.modal.notDetected')}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.form.category')}</p>
                            <p className="text-base text-gray-900 capitalize">{analysisResult.category ? t(`supplements.categories.${analysisResult.category}`, analysisResult.category.replace('_', ' ')) : t('supplements.categories.other')}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.form.form')}</p>
                            <p className="text-base text-gray-900 capitalize">{analysisResult.dosage_form ? t(`supplements.forms.${analysisResult.dosage_form}`, analysisResult.dosage_form) : t('supplements.modal.notDetected')}</p>
                          </div>
                        </div>
                      </div>

                      {/* Active Ingredients (Read-only display) */}
                      {analysisResult.active_ingredients && analysisResult.active_ingredients.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('supplements.modal.activeIngredients')}</h3>
                          <div className="bg-surface-container border border-gray-200 rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('supplements.modal.ingredient')}</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('supplements.modal.amount')}</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('supplements.modal.dailyValue')}</th>
                                </tr>
                              </thead>
                              <tbody className="bg-surface-container divide-y divide-gray-200">
                                {analysisResult.active_ingredients.map((ingredient, index) => (
                                  <tr key={index}>
                                    <td className="px-4 py-3 text-sm text-gray-900">{ingredient.name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{ingredient.amount}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {ingredient.daily_value_percentage ? `${ingredient.daily_value_percentage}%` : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Dosage Information */}
                      <div className="bg-surface-container border border-gray-200 rounded-lg p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('supplements.modal.dosageInfo')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.form.servingSize')}</p>
                            <p className="text-base text-gray-900">{analysisResult.serving_size || t('supplements.modal.notDetected')}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.modal.servingsPerContainer')}</p>
                            <p className="text-base text-gray-900">{analysisResult.servings_per_container || t('supplements.modal.notDetected')}</p>
                          </div>
                          <div className="col-span-1 md:col-span-2">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('supplements.modal.recommendedDosage')}</p>
                            <p className="text-base text-gray-900">{analysisResult.recommended_dosage || t('supplements.modal.notDetected')}</p>
                          </div>
                        </div>
                      </div>

                      {/* Default Schedule Notice */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-green-900 mb-2">📅 {t('supplements.modal.defaultSchedule')}</h3>
                        <p className="text-sm text-green-800">
                          {t('supplements.modal.defaultScheduleDesc')}
                        </p>
                      </div>

                      {/* Warnings and Allergens (Read-only) */}
                      {analysisResult.warnings && analysisResult.warnings.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-red-900 mb-2">⚠️ {t('supplements.modal.warnings')}</h3>
                          <ul className="list-disc list-inside space-y-1">
                            {analysisResult.warnings.map((warning, index) => (
                              <li key={index} className="text-sm text-red-700">{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {analysisResult.allergen_info && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-yellow-900 mb-2">{t('supplements.modal.allergenInfo')}</h3>
                          <p className="text-sm text-yellow-800">{analysisResult.allergen_info}</p>
                        </div>
                      )}

                      {/* Analysis Metadata */}
                      <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
                        <p><strong>{t('supplements.modal.processingTime')}:</strong> {analysisResult.processing_time_ms}ms</p>
                        <p><strong>{t('supplements.modal.apiCost')}:</strong> ${analysisResult.api_cost.toFixed(6)}</p>
                        <p><strong>{t('supplements.modal.model')}:</strong> {analysisResult.model_used}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveSupplement}
                      disabled={isSaving}
                      className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('common.saving')}
                        </>
                      ) : (
                        t('supplements.modal.saveToMy')
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setAnalysisResult(null)
                        setEditableAnalysis(null)
                      }}
                      disabled={isSaving}
                      className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('supplements.modal.skip')}
                    </button>
                    <button
                      onClick={handleCloseModal}
                      disabled={isSaving}
                      className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-container border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">{t('supplements.modal.addManually')}</h2>
              <button
                onClick={() => setShowManualEntry(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('supplements.form.basicInfo')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('supplements.form.name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={manualForm.name}
                      onChange={(e) => setManualForm({...manualForm, name: e.target.value})}
                      placeholder={t('supplements.form.namePlaceholder')}
                      className="w-full px-3 py-2 focus:ring-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('supplements.form.brandOptional')}
                    </label>
                    <input
                      type="text"
                      value={manualForm.brand}
                      onChange={(e) => setManualForm({...manualForm, brand: e.target.value})}
                      placeholder={t('supplements.form.brandPlaceholder')}
                      className="w-full px-3 py-2 focus:ring-2"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('supplements.form.category')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={manualForm.category}
                        onChange={(e) => setManualForm({...manualForm, category: e.target.value})}
                        className="w-full px-3 py-2 focus:ring-2"
                      >
                        <option value="vitamin">{t('supplements.categories.vitamin')}</option>
                        <option value="mineral">{t('supplements.categories.mineral')}</option>
                        <option value="herbal">{t('supplements.categories.herbal')}</option>
                        <option value="protein">{t('supplements.categories.protein')}</option>
                        <option value="amino_acid">{t('supplements.categories.amino_acid')}</option>
                        <option value="probiotic">{t('supplements.categories.probiotic')}</option>
                        <option value="omega_fatty_acid">{t('supplements.categories.omega_fatty_acid')}</option>
                        <option value="other">{t('supplements.categories.other')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('supplements.form.form')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={manualForm.dosage_form}
                        onChange={(e) => setManualForm({...manualForm, dosage_form: e.target.value})}
                        className="w-full px-3 py-2 focus:ring-2"
                      >
                        <option value="capsule">{t('supplements.forms.capsule')}</option>
                        <option value="tablet">{t('supplements.forms.tablet')}</option>
                        <option value="softgel">{t('supplements.forms.softgel')}</option>
                        <option value="gummy">{t('supplements.forms.gummy')}</option>
                        <option value="powder">{t('supplements.forms.powder')}</option>
                        <option value="liquid">{t('supplements.forms.liquid')}</option>
                        <option value="other">{t('supplements.forms.other')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('supplements.form.servingSizeOptional')}
                    </label>
                    <input
                      type="text"
                      value={manualForm.serving_size}
                      onChange={(e) => setManualForm({...manualForm, serving_size: e.target.value})}
                      placeholder={t('supplements.form.servingSizePlaceholder')}
                      className="w-full px-3 py-2 focus:ring-2"
                    />
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('supplements.form.dailySchedule')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('supplements.form.scheduleHint')}</p>

                <div className="space-y-4">
                  {/* Morning */}
                  <div className="bg-surface-container rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                          <span className="text-2xl">🌅</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('supplements.schedule.morning')}
                        </label>
                        <input
                          type="number"
                          value={manualForm.morning_quantity || ''}
                          onChange={(e) => setManualForm({...manualForm, morning_quantity: e.target.value ? parseFloat(e.target.value) : null})}
                          min="0"
                          step="0.5"
                          placeholder="0"
                          className="w-full px-3 py-2 focus:ring-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Midday */}
                  <div className="bg-surface-container rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <span className="text-2xl">☀️</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('supplements.schedule.midday')}
                        </label>
                        <input
                          type="number"
                          value={manualForm.midday_quantity || ''}
                          onChange={(e) => setManualForm({...manualForm, midday_quantity: e.target.value ? parseFloat(e.target.value) : null})}
                          min="0"
                          step="0.5"
                          placeholder="0"
                          className="w-full px-3 py-2 focus:ring-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Evening */}
                  <div className="bg-surface-container rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-2xl">🌙</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('supplements.schedule.evening')}
                        </label>
                        <input
                          type="number"
                          value={manualForm.evening_quantity || ''}
                          onChange={(e) => setManualForm({...manualForm, evening_quantity: e.target.value ? parseFloat(e.target.value) : null})}
                          min="0"
                          step="0.5"
                          placeholder="0"
                          className="w-full px-3 py-2 focus:ring-2"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('supplements.form.quantityUnit')}
                    </label>
                    <select
                      value={manualForm.quantity_unit}
                      onChange={(e) => setManualForm({...manualForm, quantity_unit: e.target.value})}
                      className="w-full px-3 py-2 focus:ring-2"
                    >
                      <option value="serving">{t('supplements.units.serving')}</option>
                      <option value="capsule">{t('supplements.units.capsule')}</option>
                      <option value="tablet">{t('supplements.units.tablet')}</option>
                      <option value="gummy">{t('supplements.units.gummy')}</option>
                      <option value="scoop">{t('supplements.units.scoop')}</option>
                      <option value="ml">{t('supplements.units.ml')}</option>
                      <option value="tsp">{t('supplements.units.tsp')}</option>
                      <option value="tbsp">{t('supplements.units.tbsp')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Start Date & Notes */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('supplements.form.startDate')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={manualForm.start_date}
                    onChange={(e) => setManualForm({...manualForm, start_date: e.target.value})}
                    className="w-full px-3 py-2 focus:ring-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('supplements.form.notesOptional')}
                  </label>
                  <textarea
                    value={manualForm.notes}
                    onChange={(e) => setManualForm({...manualForm, notes: e.target.value})}
                    placeholder={t('supplements.form.notesPlaceholder')}
                    rows="3"
                    className="w-full px-3 py-2 focus:ring-2"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSaveManualEntry}
                  disabled={isSaving}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.saving')}
                    </>
                  ) : (
                    t('supplements.form.saveSupplement')
                  )}
                </button>
                <button
                  onClick={() => setShowManualEntry(false)}
                  disabled={isSaving}
                  className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Supplement Modal */}
      {editingSupplement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-container border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">{t('supplements.modal.editSupplement')}</h2>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Supplement Info (Read-only) */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('supplements.modal.supplementInfo')}</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-600">{t('supplements.form.name')}:</span>
                    <p className="text-gray-900">{editingSupplement.supplement.name}</p>
                  </div>
                  {editingSupplement.supplement.brand && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">{t('supplements.form.brand')}:</span>
                      <p className="text-gray-900">{editingSupplement.supplement.brand}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                {/* Time-Based Scheduling */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('supplements.form.supplementSchedule')}</h3>
                  <p className="text-sm text-gray-600 mb-4">{t('supplements.form.scheduleHintEdit')}</p>

                  <div className="space-y-4">
                    {/* Morning */}
                    <div className="bg-surface-container rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                            <span className="text-2xl">🌅</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('supplements.form.morningQuantity')}</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={editForm.morning_quantity || ''}
                            onChange={(e) => setEditForm({ ...editForm, morning_quantity: e.target.value ? parseFloat(e.target.value) : null })}
                            className="w-full px-3 py-2 focus:ring-2 text-sm"
                            placeholder={t('supplements.form.quantityPlaceholder')}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Midday */}
                    <div className="bg-surface-container rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                            <span className="text-2xl">☀️</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('supplements.form.middayQuantity')}</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={editForm.midday_quantity || ''}
                            onChange={(e) => setEditForm({ ...editForm, midday_quantity: e.target.value ? parseFloat(e.target.value) : null })}
                            className="w-full px-3 py-2 focus:ring-2 text-sm"
                            placeholder={t('supplements.form.quantityPlaceholder')}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Evening */}
                    <div className="bg-surface-container rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-2xl">🌙</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('supplements.form.eveningQuantity')}</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={editForm.evening_quantity || ''}
                            onChange={(e) => setEditForm({ ...editForm, evening_quantity: e.target.value ? parseFloat(e.target.value) : null })}
                            className="w-full px-3 py-2 focus:ring-2 text-sm"
                            placeholder={t('supplements.form.quantityPlaceholder')}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Unit */}
                    <div className="bg-surface-container rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('supplements.form.unit')}</label>
                      <select
                        value={editForm.quantity_unit}
                        onChange={(e) => setEditForm({ ...editForm, quantity_unit: e.target.value })}
                        className="w-full px-3 py-2 focus:ring-2"
                      >
                        <option value="serving">{t('supplements.units.servingLower')}</option>
                        <option value="tablet">{t('supplements.units.tabletLower')}</option>
                        <option value="capsule">{t('supplements.units.capsuleLower')}</option>
                        <option value="softgel">{t('supplements.units.softgelLower')}</option>
                        <option value="gummy">{t('supplements.units.gummyLower')}</option>
                        <option value="ml">{t('supplements.units.mlLower')}</option>
                        <option value="scoop">{t('supplements.units.scoopLower')}</option>
                        <option value="drop">{t('supplements.units.dropLower')}</option>
                      </select>
                    </div>

                    {/* Total Display */}
                    <p className="text-xs text-gray-500 text-center">
                      {(() => {
                        const total = (editForm.morning_quantity || 0) +
                                    (editForm.midday_quantity || 0) +
                                    (editForm.evening_quantity || 0)
                        return total > 0 ? t('supplements.form.totalDaily', { total, unit: editForm.quantity_unit || t('supplements.units') }) : t('supplements.form.setAtLeastOne')
                      })()}
                    </p>
                  </div>
                </div>

                {/* Start and End Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('supplements.form.startDate')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editForm.start_date}
                      onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                      className="w-full px-3 py-2 focus:ring-2"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('supplements.form.startDateHint')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('supplements.form.endDateOptional')}
                    </label>
                    <input
                      type="date"
                      value={editForm.end_date}
                      onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                      className="w-full px-3 py-2 focus:ring-2"
                      min={editForm.start_date}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('supplements.form.endDateHint')}</p>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('supplements.form.notes')}
                  </label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 focus:ring-2"
                    placeholder={t('supplements.form.notesPlaceholderEdit')}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMutation.isPending ? t('common.saving') : t('common.saveChanges')}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={updateMutation.isPending}
                  className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {supplements?.data?.user_supplements?.length > 0 ? (
        <>
          {/* Active Supplements Section */}
          {supplements.data.user_supplements.filter(us => isCurrentlyTaking(us)).length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('supplements.currentlyTaking')} ({supplements.data.user_supplements.filter(us => isCurrentlyTaking(us)).length})</h2>
              <div className="grid gap-4">
                {supplements.data.user_supplements.filter(us => isCurrentlyTaking(us)).map((userSupplement) => (
            <div key={userSupplement.id} className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <HeartIcon className="h-6 w-6 text-primary-600 mr-3" />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900">{userSupplement.supplement.name}</h3>
                          {isCurrentlyTaking(userSupplement) ? (
                            <span className="badge badge-success">{t('supplements.status.currentlyTaking')}</span>
                          ) : (
                            <span className="badge badge-gray">{t('supplements.status.stopped')}</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 ml-4">
                          {t('supplements.added')}: {new Date(userSupplement.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {getTimeBasedSchedule(userSupplement)}
                      </p>
                      {userSupplement.start_date && (
                        <p className="text-xs text-gray-600 mt-1">
                          {isCurrentlyTaking(userSupplement) ? t('supplements.takingFor') : t('supplements.tookFor')}: {calculateDuration(userSupplement.start_date, userSupplement.end_date)}
                          {userSupplement.end_date && !isCurrentlyTaking(userSupplement) && ` (${t('supplements.stoppedOn')}: ${new Date(userSupplement.end_date).toLocaleDateString()})`}
                        </p>
                      )}
                      {(() => {
                        const consumption = calculateTotalConsumed(userSupplement)
                        return consumption && (
                          <p className="text-xs text-indigo-600 font-medium mt-1">
                            {t('supplements.totalConsumed')}: {consumption.total} {consumption.unit} {t('supplements.over')} {consumption.days} {t('supplements.daysLabel')}
                          </p>
                        )
                      })()}
                      {userSupplement.notes && (
                        <p className="text-xs text-gray-400 mt-1">
                          <span className="font-medium">{t('common.notes')}:</span> {translatedNotes[userSupplement.id] || userSupplement.notes}
                        </p>
                      )}
                      {/* Knowledge Insights Panel */}
                      {(() => {
                        const supplementId = userSupplement.supplement.id
                        const isExpanded = expandedKnowledge[supplementId]
                        const knowledge = knowledgeData[supplementId]

                        const toggleKnowledge = async () => {
                          if (!isExpanded && !knowledge) {
                            const currentLang = contentLanguage(i18n?.language)
                            try {
                              // Fetch English base first, then translate if needed
                              const response = await supplementsAPI.getKnowledge(supplementId, 'en')
                              let data = response.data
                              if (currentLang !== 'en' && data?.id) {
                                try {
                                  const translated = await supplementsAPI.translateKnowledge(supplementId, currentLang)
                                  data = translated.data
                                } catch (translErr) {
                                  console.warn(`[Supplements] Translation failed for knowledge ${supplementId}, using English:`, translErr)
                                }
                              }
                              setKnowledgeData(prev => ({ ...prev, [supplementId]: data }))
                            } catch (err) {
                              setKnowledgeData(prev => ({ ...prev, [supplementId]: null }))
                            }
                          }
                          setExpandedKnowledge(prev => ({ ...prev, [supplementId]: !isExpanded }))
                        }

                        return (
                          <>
                            <button
                              onClick={toggleKnowledge}
                              className="mt-2 flex items-center text-xs text-indigo-600 hover:text-indigo-800"
                            >
                              <LightBulbIcon className="h-3.5 w-3.5 mr-1" />
                              {t('supplements.knowledge.insights')}
                              {isExpanded ? <ChevronUpIcon className="h-3 w-3 ml-1" /> : <ChevronDownIcon className="h-3 w-3 ml-1" />}
                            </button>
                            {isExpanded && (
                              <div className="mt-2 bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs space-y-2">
                                {knowledge ? (
                                  <>
                                    {knowledge.summary && (
                                      <p className="font-medium text-indigo-900">{knowledge.summary}</p>
                                    )}
                                    {knowledge.purpose && (
                                      <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.purpose')}:</span> {knowledge.purpose}</div>
                                    )}
                                    {knowledge.expected_effects && (
                                      <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.effects')}:</span> {knowledge.expected_effects}</div>
                                    )}
                                    {knowledge.risks_of_overdose && (
                                      <div className="text-amber-700"><span className="font-semibold">{t('supplements.knowledge.overdoseRisk')}:</span> {knowledge.risks_of_overdose}</div>
                                    )}
                                    {knowledge.upper_tolerable_limit && (
                                      <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.upperLimit')}:</span> {knowledge.upper_tolerable_limit}</div>
                                    )}
                                    {knowledge.key_interactions && (
                                      <div className="text-red-700"><span className="font-semibold">{t('supplements.knowledge.interactions')}:</span> {knowledge.key_interactions}</div>
                                    )}
                                    {knowledge.best_taken_with && (
                                      <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.bestWith')}:</span> {knowledge.best_taken_with}</div>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-gray-500 italic">{t('supplements.knowledge.notGenerated')}</p>
                                )}
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {isCurrentlyTaking(userSupplement) && (
                      <button
                        onClick={() => handleStopTaking(userSupplement)}
                        disabled={updateMutation.isPending}
                        className="btn-outline-warning disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('supplements.stopTaking')}
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(userSupplement)}
                      className="btn-outline"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleDeleteClick(userSupplement)}
                      disabled={deleteMutation.isPending}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      title={t('supplements.deleteSupplement')}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
                ))}
              </div>
            </div>
          )}

          {/* Stopped Supplements Section */}
          {supplements.data.user_supplements.filter(us => !isCurrentlyTaking(us)).length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('supplements.stoppedTaking')} ({supplements.data.user_supplements.filter(us => !isCurrentlyTaking(us)).length})</h2>
              <div className="grid gap-4">
                {supplements.data.user_supplements.filter(us => !isCurrentlyTaking(us)).map((userSupplement) => (
                  <div key={userSupplement.id} className="card opacity-75">
                    <div className="card-body">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <HeartIcon className="h-6 w-6 text-gray-400 mr-3" />
                          <div className="flex-1">
                            <div className="flex items-baseline justify-between">
                              <div className="flex items-center space-x-2">
                                <h3 className="text-lg font-medium text-gray-900">{userSupplement.supplement.name}</h3>
                                <span className="badge badge-gray">{t('supplements.status.stopped')}</span>
                              </div>
                              <span className="text-xs text-gray-400 ml-4">
                                {t('supplements.added')}: {new Date(userSupplement.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {getTimeBasedSchedule(userSupplement)}
                            </p>
                            {userSupplement.start_date && (
                              <p className="text-xs text-gray-600 mt-1">
                                {t('supplements.tookFor')}: {calculateDuration(userSupplement.start_date, userSupplement.end_date)}
                                {userSupplement.end_date && ` (${t('supplements.stoppedOn')}: ${new Date(userSupplement.end_date).toLocaleDateString()})`}
                              </p>
                            )}
                            {(() => {
                              const consumption = calculateTotalConsumed(userSupplement)
                              return consumption && (
                                <p className="text-xs text-gray-600 font-medium mt-1">
                                  {t('supplements.totalConsumed')}: {consumption.total} {consumption.unit} {t('supplements.over')} {consumption.days} {t('supplements.daysLabel')}
                                </p>
                              )
                            })()}
                            {userSupplement.notes && (
                              <p className="text-xs text-gray-400 mt-1">
                                <span className="font-medium">{t('common.notes')}:</span> {translatedNotes[userSupplement.id] || userSupplement.notes}
                              </p>
                            )}
                            {/* Knowledge Insights Panel (Stopped) */}
                            {(() => {
                              const supplementId = userSupplement.supplement.id
                              const isExpanded = expandedKnowledge[supplementId]
                              const knowledge = knowledgeData[supplementId]

                              const toggleKnowledge = async () => {
                                if (!isExpanded && !knowledge) {
                                  const currentLang = contentLanguage(i18n?.language)
                                  try {
                                    // Fetch English base first, then translate if needed
                                    const response = await supplementsAPI.getKnowledge(supplementId, 'en')
                                    let data = response.data
                                    if (currentLang !== 'en' && data?.id) {
                                      try {
                                        const translated = await supplementsAPI.translateKnowledge(supplementId, currentLang)
                                        data = translated.data
                                      } catch (translErr) {
                                        console.warn(`[Supplements] Translation failed for knowledge ${supplementId}, using English:`, translErr)
                                      }
                                    }
                                    setKnowledgeData(prev => ({ ...prev, [supplementId]: data }))
                                  } catch (err) {
                                    setKnowledgeData(prev => ({ ...prev, [supplementId]: null }))
                                  }
                                }
                                setExpandedKnowledge(prev => ({ ...prev, [supplementId]: !isExpanded }))
                              }

                              return (
                                <>
                                  <button
                                    onClick={toggleKnowledge}
                                    className="mt-2 flex items-center text-xs text-indigo-600 hover:text-indigo-800"
                                  >
                                    <LightBulbIcon className="h-3.5 w-3.5 mr-1" />
                                    {t('supplements.knowledge.insights')}
                                    {isExpanded ? <ChevronUpIcon className="h-3 w-3 ml-1" /> : <ChevronDownIcon className="h-3 w-3 ml-1" />}
                                  </button>
                                  {isExpanded && (
                                    <div className="mt-2 bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs space-y-2">
                                      {knowledge ? (
                                        <>
                                          {knowledge.summary && (
                                            <p className="font-medium text-indigo-900">{knowledge.summary}</p>
                                          )}
                                          {knowledge.purpose && (
                                            <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.purpose')}:</span> {knowledge.purpose}</div>
                                          )}
                                          {knowledge.expected_effects && (
                                            <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.effects')}:</span> {knowledge.expected_effects}</div>
                                          )}
                                          {knowledge.risks_of_overdose && (
                                            <div className="text-amber-700"><span className="font-semibold">{t('supplements.knowledge.overdoseRisk')}:</span> {knowledge.risks_of_overdose}</div>
                                          )}
                                          {knowledge.upper_tolerable_limit && (
                                            <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.upperLimit')}:</span> {knowledge.upper_tolerable_limit}</div>
                                          )}
                                          {knowledge.key_interactions && (
                                            <div className="text-red-700"><span className="font-semibold">{t('supplements.knowledge.interactions')}:</span> {knowledge.key_interactions}</div>
                                          )}
                                          {knowledge.best_taken_with && (
                                            <div><span className="font-semibold text-gray-700">{t('supplements.knowledge.bestWith')}:</span> {knowledge.best_taken_with}</div>
                                          )}
                                        </>
                                      ) : (
                                        <p className="text-gray-500 italic">{t('supplements.knowledge.notGenerated')}</p>
                                      )}
                                    </div>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleEditClick(userSupplement)}
                            className="btn-outline"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(userSupplement)}
                            disabled={deleteMutation.isPending}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                            title={t('supplements.deleteSupplement')}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <HeartIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('supplements.noSupplements')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('supplements.noSupplementsHint')}
          </p>
        </div>
      )}
    </div>
  )
}